import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { CHALLENGE_TRACKS, DEFAULT_TRACK, type MyChallenge, type ChallengeEligibility } from '@core/economy/challengeTracks';
import { useEquippedCompanion } from '../../hooks/useEquippedCompanion';
import { useBehaviorTrend } from '../../hooks/useBehaviorTrend';
import { CastAvatar } from '../_components/cast';
import { buildSeries, shortLabel, backfillNote, volumeCaveat, type MetricSeries } from './behaviorTrend';
import { attentionCount, fmtHabitPnl, fmtNatural, habitExpression, habitPnl, habitSpeech, habitState, habitTabs,
  isCurrent, naturalCaption, RATIO_KEYS, showsWarning } from './homeHabitHero';

const GOLD = 'var(--cs-gold-on-ink)';
const IVORY = 'var(--cs-accent-ink)';
// 아이보리(--cs-accent-ink #FBF8F1)의 알파 파생. color-mix()는 iOS 15 Safari(SE3 실기)가 모르므로 rgba 리터럴을 쓴다 — 기존 HomeScreen 히어로와 같은 방식.
const inkAlpha = (pct: number) => `rgba(251, 248, 241, ${pct / 100})`;
const DIM = inkAlpha(62);
const FAINT = inkAlpha(20);
// 최소 높이를 고정하지 않는다 — 416px 예약은 0회·실패 화면에 빈 녹색 공간을 크게 남겼다(2026-10-08 e2e 실측). 로딩 스켈레톤이 완성 높이(~230px)를 대신 잡는다.
const hero: CSSProperties = { background: 'var(--cs-hero-ink)', borderRadius: 22, padding: '20px 20px 12px',
  color: IVORY, marginBottom: 14, boxShadow: 'var(--cs-shadow-hero)', boxSizing: 'border-box' };
const inbox: CSSProperties = { background: inkAlpha(7), borderRadius: 12, padding: 12, marginTop: 8 };
const plain: CSSProperties = { background: 'transparent', border: 0, color: 'inherit', cursor: 'pointer', padding: 0, font: 'inherit' };
const detailRoute = '/portfolio-analysis?tab=retrospective';

export type HomeHabitHeroProps = {
  challenge: MyChallenge | null;
  challengeLoading: boolean;
  challengeError: boolean;
  eligibility: ChallengeEligibility | null;
  refreshChallenge: () => Promise<unknown>;
  joinChallenge: () => Promise<unknown>;
  lossCount: number;
};

/** 미션 판정·참가·재시도는 기존 홈 데이터를 받는다. 히어로 주인공 아래 작은 줄로 표시한다. */
function Mission(p: HomeHabitHeroProps) {
  const nav = useNavigate();
  const [joining, setJoining] = useState(false);
  const c = p.challenge;
  const track = CHALLENGE_TRACKS[c?.track ?? DEFAULT_TRACK] ?? CHALLENGE_TRACKS[DEFAULT_TRACK];
  if (p.challengeLoading) return <div aria-label="기록 미션 불러오는 중" style={{ height: 42, marginTop: 14 }} />;
  const title = p.challengeError ? '기록을 불러오지 못했어요'
    : c?.status === 'active' ? track.title : c?.status === 'success' ? '미션을 완주했어요'
      : c?.status === 'failed' ? '미션이 종료됐어요'
        : p.eligibility?.can_join ? CHALLENGE_TRACKS[DEFAULT_TRACK].title : '복기할 기록이 아직 없어요';
  const note = p.challengeError ? '' : c?.status === 'active'
    ? `${c.required_count > 0 ? `복기 기록 ${c.progress_count}/${c.required_count}건 · ` : ''}${Date.now() >= Date.parse(c.window_ends_at)
      ? '기간 종료 · 결과 확인 중' : c.required_count > 0 && c.progress_count >= c.required_count
        ? '기록을 남겼어요 · 종료일에 확정돼요' : '배지 + EXP'}`
    : c?.status === 'success' ? '조건 달성 · 배지 + EXP · 시즌 첫 완주면 뽑기권 1장'
      : c?.status === 'failed' ? '기간 안에 복기 기록이 없었어요 · 결과: 조건 미충족'
        : p.eligibility?.can_join ? '7일 안에 지난 손실 매도 한 건을 돌아봐요 · 배지 + EXP'
          : '손실 매도가 생기면 미션을 열 수 있어요';
  return <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, paddingTop: 12,
    boxShadow: `inset 0 1px 0 ${inkAlpha(12)}`, fontSize: 12 }}>
    {c?.status === 'active' && !p.challengeError && <svg width="30" height="30" viewBox="0 0 36 36" style={{ flexShrink: 0 }} aria-label={`미션 ${c.day_index}/${c.total_days}일`}>
      <circle cx="18" cy="18" r="15.9" fill="none" stroke={inkAlpha(15)} strokeWidth="3.2" />
      <circle cx="18" cy="18" r="15.9" fill="none" stroke={GOLD} strokeWidth="3.2" strokeLinecap="round"
        strokeDasharray={`${Math.round(c.day_index / Math.max(1, c.total_days) * 100)} 100`} transform="rotate(-90 18 18)" />
      <text x="18" y="21" textAnchor="middle" fontSize="8.5" fontWeight="800" fill={IVORY}>{c.day_index}/{c.total_days}</text>
    </svg>}
    <div style={{ flex: 1, minWidth: 0 }}><b>{title}</b>{note && <div style={{ color: DIM, fontSize: 11, marginTop: 2 }}>{note}</div>}
      {!p.challengeError && c && c.status !== 'active' && !p.eligibility?.can_join && <div style={{ color: DIM }}>복기할 기록이 아직 없어요</div>}
    </div>
    {p.challengeError ? <button style={plain} onClick={() => void p.refreshChallenge()}>다시 시도</button>
      : c?.status !== 'active' && p.eligibility?.can_join ? <button style={{ ...plain, color: GOLD, flexShrink: 0 }} disabled={joining} onClick={async () => {
        setJoining(true);
        try { await p.joinChallenge(); } catch { await p.refreshChallenge(); } finally { setJoining(false); }
      }}>{joining ? '시작 중' : c ? '다시 시작' : '미션 시작'}</button>
        : p.lossCount > 0 ? <button style={plain} aria-label={`복기할 손실 ${p.lossCount}건`} onClick={() => nav('/sim/reflect')}>→</button> : null}
  </div>;
}

/** 시안의 252×108, 14px 막대, 6칸 배치를 유지한다. 높이는 원값으로 계산한다. */
function RoundBars({ s, count }: { s: MetricSeries; count: number }) {
  const points = s.points.slice(-6);
  const start = Math.max(0, count - 6);
  const max = Math.max(1, s.threshold, ...points.filter((v): v is number => v != null)) * 1.15;
  const y = (v: number) => 64 - Math.max(0, v) / max * 54;
  const gap = (204 - 6 * 14) / 7;
  const color = s.delta === 'improved' ? GOLD : IVORY;
  const current = isCurrent(s);
  return <svg width="252" height="108" viewBox="0 0 252 108" style={{ maxWidth: '100%', display: 'block', overflow: 'visible' }} role="img" aria-label={`${shortLabel(s.key, s.label)} 회차별 막대, ${naturalCaption(s.key)}, 기준 ${fmtNatural(s.key, s.threshold)}`}>
    <line x1="0" x2="204" y1={y(s.threshold)} y2={y(s.threshold)} stroke={DIM} strokeDasharray="3 4" />
    <text x="252" y={y(s.threshold) + 3} textAnchor="end" fontSize="9.5" fill={DIM}>기준 {fmtNatural(s.key, s.threshold)}</text>
    {Array.from({ length: 6 }, (_, i) => {
      const x = gap + i * (14 + gap), v = points[i];
      const last = i === points.length - 1;
      if (v == null) return <g key={i} data-habit-empty="true">
        <rect x={x} y="10" width="14" height="54" rx="4" fill={inkAlpha(7)} />
        <text x={x + 7} y="96" textAnchor="middle" fontSize="9.5" fill={inkAlpha(35)}>{start + i + 1}회</text>
      </g>;
      const alert = last && showsWarning(s);
      // 보유 기간은 기준보다 낮을 때 위험하다. 초과 윗부분 대신 현재 막대와 배지로 표시한다.
      const split = alert && s.direction === 'lower' && v > s.threshold;
      return <g key={i} data-habit-bar="true">
        <title>{`${start + i + 1}회: ${fmtNatural(s.key, v)}`}</title>
        {split ? <><rect x={x} y={y(s.threshold)} width="14" height={64 - y(s.threshold)} rx="4" fill={FAINT} />
          <rect data-habit-excess="true" x={x} y={y(v)} width="14" height={y(s.threshold) - y(v) + 4} rx="4" fill={IVORY} /></>
          : <rect x={x} y={y(v)} width="14" height={64 - y(v)} rx="4" fill={last && current ? color : FAINT} />}
        {alert && <g data-habit-warning="true"><circle cx={x + 7} cy={y(v) - 10} r="6" fill={IVORY} />
          <text x={x + 7} y={y(v) - 6.5} textAnchor="middle" fontSize="9" fontWeight="900" fill="var(--cs-hero-ink)">!</text></g>}
        <text x={x + 7} y={64 + (last ? 19 : 15)} textAnchor="middle" fontSize={last ? 18 : 10.5} fontWeight={last ? 800 : 700} fill={last ? color : DIM}>
          {RATIO_KEYS.has(s.key) ? fmtNatural(s.key, v).replace(/번$/, '') : fmtNatural(s.key, v)}</text>
        <text x={x + 7} y="96" textAnchor="middle" fontSize="9.5" fill={last ? IVORY : DIM}>{last ? '최근' : `${start + i + 1}회`}</text>
      </g>;
    })}
  </svg>;
}

function Gauge({ s }: { s: MetricSeries }) {
  const value = s.points[s.points.length - 1];
  const max = RATIO_KEYS.has(s.key) || s.key === 'concentration' ? 100 : Math.max(1, value ?? 0, s.threshold) * 1.2;
  const color = s.exceeded ? IVORY : GOLD;
  return <>
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 4 }}>
      <span style={{ color: DIM, fontSize: 12 }}>지금</span><b style={{ fontSize: 26, color }}>{fmtNatural(s.key, value)}</b></div>
    <div role="img" aria-label={`${naturalCaption(s.key)} ${fmtNatural(s.key, value)}, 기준 ${fmtNatural(s.key, s.threshold)}`}
      style={{ position: 'relative', height: 12, borderRadius: 6, background: inkAlpha(14), marginTop: 8 }}>
      {value != null && <div style={{ width: `${Math.min(100, Math.max(0, value) / max * 100)}%`, height: '100%', borderRadius: 6, background: color }} />}
      <div style={{ position: 'absolute', left: `${s.threshold / max * 100}%`, top: -5, width: 2, height: 22, borderRadius: 1, background: GOLD }} />
    </div>
    <div style={{ color: DIM, fontSize: 11, marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
      <span>금색 선 = 기준 {fmtNatural(s.key, s.threshold)}</span><span>다음 복기부터 변화가 그려져요</span></div>
  </>;
}

export default function HomeHabitHero(props: HomeHabitHeroProps) {
  const nav = useNavigate();
  const trend = useBehaviorTrend();
  const companion = useEquippedCompanion(['home_mascot', 'nick_avatar']);
  const [selected, setSelected] = useState<string | null>(null);
  const rounds = trend.data?.rounds ?? [];
  const series = buildSeries(rounds);
  const tabs = habitTabs(series);
  const s = tabs.find((t) => t.key === selected) ?? tabs[0];
  const state = habitState(rounds, trend.isPending || companion.isLoading, trend.isError);
  const count = rounds.length;
  const attention = attentionCount(series);
  const pnl = s ? habitPnl(rounds, s.key) : null;
  const say = habitSpeech(s, count);
  const color = !count || (s && isCurrent(s) && s.delta === 'improved') ? GOLD : IVORY;
  const historyNote = backfillNote(rounds);
  return <section aria-label="내 매매 습관" data-habit-state={state} style={hero}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: inkAlpha(66) }}>
      <span>내 매매 습관</span>
      <span>{state === 'loading' || state === 'failed' ? '습관 기록' : count ? `복기 ${count}회차` : '복기 전'}
        {state !== 'loading' && state !== 'failed' && attention > 0 && <> · <b style={{ color: IVORY }}>주의 {attention}</b></>}</span>
    </div>
    {state === 'loading' ? <div role="status" aria-label="습관 기록 불러오는 중" style={{ marginTop: 14 }}>
      <div style={{ height: 72, borderRadius: 12, background: inkAlpha(7) }} /><div style={{ ...inbox, height: 150 }} /><div style={{ height: 54, marginTop: 14 }} />
    </div> : state === 'failed' ? <div role="alert" style={{ marginTop: 28, lineHeight: 1.7 }}>
      습관 기록을 불러오지 못했어요 · <button style={{ ...plain, color: GOLD }} onClick={() => void trend.refetch()}>다시 시도</button>
    </div> : <>
      <button onClick={() => count && nav(detailRoute)} disabled={!count} style={{ ...plain, display: 'flex', textAlign: 'left', gap: 12, alignItems: 'center', marginTop: 14, width: '100%', cursor: count ? 'pointer' : 'default' }}
        title={volumeCaveat(rounds) ?? undefined}>
        <CastAvatar id={companion.slug} expr={habitExpression(s, count)} size={56} />
        <span style={{ fontSize: 16, lineHeight: 1.5, fontWeight: 700 }}>
          {say.split(/(절반 가까이|두 배 가까이|훨씬|조금)/).map((part, i) => /^(절반 가까이|두 배 가까이|훨씬|조금)$/.test(part)
            ? <b key={i} style={{ color }}>{part}</b> : part)}
        </span>
      </button>
      {state === 'empty' ? <>
        <div style={{ ...inbox, marginTop: 14 }}>
          <div aria-hidden="true" style={{ display: 'grid', gridTemplateColumns: 'repeat(6,minmax(0,1fr))', gap: 8, height: 44 }}>
            {Array.from({ length: 6 }, (_, i) => <span key={i} style={{ height: 44, borderRadius: 4,
              background: i ? inkAlpha(7) : 'rgba(228, 197, 122, 0.35)' /* --cs-gold-on-ink #E4C57A 알파 파생 */, boxShadow: i ? 'none' : `inset 0 0 0 1.5px ${GOLD}` }} />)}
          </div><div style={{ color: DIM, fontSize: 11.5, marginTop: 8 }}>첫 칸은 복기 한 번이면 채워져요 · 습관 9가지를 재요</div>
        </div>
        <button style={{ ...plain, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: 48,
          borderRadius: 12, background: GOLD, color: 'var(--cs-ink)', fontSize: 15, fontWeight: 800, margin: '14px 0 6px' }} onClick={() => nav('/sim/reflect')}>첫 복기 하러 가기</button>
      </> : <>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 12, boxShadow: `inset 0 -1px 0 ${inkAlpha(12)}`, gap: 14 }}>
          {tabs.map((tab) => <button key={tab.key} aria-pressed={tab.key === s?.key} onClick={() => setSelected(tab.key)}
            style={{ ...plain, fontSize: 13, fontWeight: 700, padding: '8px 2px', minHeight: 30,
              color: tab.key === s?.key ? IVORY : DIM, boxShadow: tab.key === s?.key ? `inset 0 -2px 0 ${GOLD}` : 'none' }}>{shortLabel(tab.key, tab.label)}</button>)}
          <button style={{ ...plain, marginLeft: 'auto', color: DIM, fontSize: 11, flexShrink: 0 }} onClick={() => nav(detailRoute)} aria-label="9가지 전체">전체 →</button>
        </div>
        {s && <div style={inbox}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 4 }}>
            <b style={{ fontSize: 13 }}>{naturalCaption(s.key)}</b>
            {pnl && <span data-testid="habit-pnl" style={{ fontSize: 11.5 }}><span style={{ color: DIM }}>손익 </span>
              {pnl.first != null && <span style={{ color: DIM }}>{fmtHabitPnl(pnl.first)} → </span>}<b style={{ fontSize: 13 }}>{fmtHabitPnl(pnl.last)}</b></span>}
          </div>
          {state === 'first' ? <Gauge s={s} /> : <div style={{ marginTop: 6 }}><RoundBars s={s} count={count} /></div>}
          {!isCurrent(s) && <div style={{ color: DIM, fontSize: 11 }}>이번 회차 미측정{s.sample && s.sample.have < s.sample.need ? ` · 표본 ${s.sample.have}/${s.sample.need}` : ''}</div>}
        </div>}
        {(historyNote || (trend.data?.skipped ?? 0) > 0) && <button style={{ ...plain, color: DIM, fontSize: 11, marginTop: 6 }} onClick={() => nav(detailRoute)}>
          {historyNote ? '사후 재구성 회차 포함' : `측정 기록 없는 ${trend.data?.skipped}회 제외`} · 상세 →</button>}
        <Mission {...props} />
      </>}
    </>}
  </section>;
}
